<template>
  <div>
    <el-row :gutter="20" v-loading="loading">
      <el-col
        v-for="item in statItems"
        :key="item.key"
        :span="6"
        style="margin-bottom: 20px"
      >
        <el-card shadow="hover" class="stat-card">
          <div class="stat-item">
            <div class="stat-label">{{ item.label }}</div>
            <div class="stat-number" :style="{ color: item.color }">
              <span v-if="item.currency">¥</span>{{ formatValue(stats[item.key], item.currency) }}
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-card shadow="never" class="trend-card" v-loading="loading">
      <div class="trend-header">
        <div>
          <div class="trend-title">最近30天用户增长和发帖增长比例</div>
          <div class="trend-subtitle">柱状图展示新增用户与新增帖子，折线展示帖子/用户比</div>
        </div>
      </div>

      <div v-if="trendData.length" ref="trendChart" class="trend-chart"></div>
      <div v-else class="trend-empty">暂无趋势数据</div>
    </el-card>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { getDashboardStats } from '../../api'

export default {
  name: 'Dashboard',
  data() {
    return {
      loading: false,
      stats: {},
      trendChart: null,
      trendRenderTimer: null,
      statItems: [
        { key: 'todayNewUsers', label: '今日新增用户数', color: '#409EFF' },
        { key: 'todayNewPosts', label: '今日新增帖子数量', color: '#67C23A' },
        { key: 'totalUsers', label: '总用户数', color: '#909399' },
        { key: 'totalPosts', label: '总帖子数', color: '#909399' },
        { key: 'certifiedPersonalUsers', label: '认证牛人数', color: '#E6A23C' },
        { key: 'certifiedOrganizationUsers', label: '认证机构数', color: '#E6A23C' },
        { key: 'todayPostRevenue', label: '今日发帖收益', color: '#F56C6C', currency: true },
        { key: 'totalPostRevenue', label: '累计发帖收益', color: '#F56C6C', currency: true }
      ]
    }
  },
  computed: {
    trendData() {
      return Array.isArray(this.stats.growthTrend30Days) ? this.stats.growthTrend30Days : []
    }
  },
  created() {
    this.fetchStats()
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
    this.scheduleRenderTrendChart()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    if (this.trendRenderTimer) {
      clearTimeout(this.trendRenderTimer)
      this.trendRenderTimer = null
    }
    if (this.trendChart) {
      this.trendChart.dispose()
      this.trendChart = null
    }
  },
  methods: {
    async fetchStats() {
      this.loading = true
      try {
        const res = await getDashboardStats()
        this.stats = res.data || {}
        this.scheduleRenderTrendChart()
      } finally {
        this.loading = false
      }
    },
    formatValue(value, currency) {
      if (currency) {
        const amount = Number(value || 0)
        return amount.toFixed(2)
      }
      return value || 0
    },
    handleResize() {
      if (this.trendChart) {
        this.trendChart.resize()
      }
    },
    scheduleRenderTrendChart() {
      if (this.trendRenderTimer) {
        clearTimeout(this.trendRenderTimer)
      }
      this.trendRenderTimer = setTimeout(() => {
        this.trendRenderTimer = null
        this.$nextTick(() => {
          window.requestAnimationFrame(() => {
            this.renderTrendChart()
          })
        })
      }, 0)
    },
    renderTrendChart() {
      const el = this.$refs.trendChart
      const trendData = this.normalizeTrendData(this.trendData)
      if (!el || !trendData.length) return

      if (!this.trendChart) {
        this.trendChart = echarts.init(el)
      }

      const sourceRows = trendData.map(item => ([
        item.label || item.date || '',
        this.safeNumber(item.newUsers),
        this.safeNumber(item.newPosts),
        this.safeNumber(item.postUserRatio)
      ]))

      this.trendChart.setOption({
        color: ['#409EFF', '#67C23A', '#E6A23C'],
        dataset: {
          source: sourceRows
        },
        grid: {
          left: 56,
          right: 64,
          top: 56,
          bottom: 54,
          containLabel: false
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          backgroundColor: 'rgba(255, 255, 255, 0.96)',
          borderColor: '#e8e8e8',
          borderWidth: 1,
          textStyle: { color: '#303133' },
          formatter(params) {
            const items = Array.isArray(params) ? params : [params]
            const title = items[0] ? items[0].axisValueLabel : ''
            const lines = items.map(item => {
              const value = item.seriesName === '发帖/用户比'
                ? Number(item.data || 0).toFixed(2)
                : Number(item.data || 0)
              const suffix = item.seriesName === '发帖/用户比' ? '' : ' 条'
              return `${item.marker}${item.seriesName}：${value}${suffix}`
            })
            return [title].concat(lines).join('<br/>')
          }
        },
        legend: {
          top: 10,
          right: 10,
          icon: 'circle',
          itemWidth: 10,
          itemHeight: 10,
          textStyle: {
            color: '#606266'
          }
        },
        xAxis: {
          type: 'category',
          boundaryGap: true,
          axisLine: {
            lineStyle: { color: '#dcdfe6' }
          },
          axisTick: { show: false },
          axisLabel: {
            color: '#909399',
            interval: 2
          }
        },
        yAxis: [
          {
            type: 'value',
            name: '新增数量',
            minInterval: 1,
            axisLabel: { color: '#909399' },
            splitLine: { lineStyle: { color: '#edf0f5' } },
            nameTextStyle: { color: '#909399' }
          },
          {
            type: 'value',
            name: '发帖/用户比',
            min: 0,
            axisLabel: {
              color: '#909399',
              formatter: '{value}'
            },
            splitLine: { show: false },
            nameTextStyle: { color: '#909399' }
          }
        ],
        series: [
          {
            name: '新增用户',
            type: 'bar',
            encode: { x: 0, y: 1 },
            barWidth: 14,
            itemStyle: {
              borderRadius: [6, 6, 0, 0]
            }
          },
          {
            name: '新增帖子',
            type: 'bar',
            encode: { x: 0, y: 2 },
            barWidth: 14,
            itemStyle: {
              borderRadius: [6, 6, 0, 0]
            }
          },
          {
            name: '发帖/用户比',
            type: 'line',
            yAxisIndex: 1,
            encode: { x: 0, y: 3 },
            smooth: true,
            symbol: 'circle',
            symbolSize: 8,
            lineStyle: {
              width: 3,
              type: 'dashed'
            },
            itemStyle: {
              borderWidth: 2,
              borderColor: '#fff'
            }
          }
        ]
      }, true)
    },
    normalizeTrendData(list) {
      if (!Array.isArray(list)) return []
      return list
        .filter(item => item && typeof item === 'object')
        .map(item => ({
          date: item.date || '',
          label: item.label || item.date || '',
          newUsers: this.safeNumber(item.newUsers),
          newPosts: this.safeNumber(item.newPosts),
          postUserRatio: this.safeNumber(item.postUserRatio)
        }))
    },
    safeNumber(value) {
      const num = Number(value)
      return Number.isFinite(num) ? num : 0
    }
  },
}
</script>

<style scoped>
.trend-card {
  margin-top: 20px;
  border-radius: 10px;
}

.trend-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.trend-title {
  color: #303133;
  font-size: 16px;
  font-weight: 700;
}

.trend-subtitle {
  margin-top: 6px;
  color: #909399;
  font-size: 12px;
}

.trend-legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 14px;
  color: #606266;
  font-size: 12px;
}

.trend-legend span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.user-dot {
  background: #409EFF;
}

.post-dot {
  background: #67C23A;
}

.ratio-dot {
  background: #E6A23C;
}

.trend-chart {
  width: 100%;
  height: 360px;
}

.trend-empty {
  padding: 60px 0;
  text-align: center;
  color: #909399;
  font-size: 14px;
}

.stat-card {
  border-radius: 10px;
}

.stat-item {
  text-align: center;
  padding: 8px 0;
}

.stat-number {
  font-size: 30px;
  font-weight: 700;
  margin-top: 8px;
  line-height: 1.2;
}

.stat-label {
  font-size: 14px;
  color: #909399;
}
</style>
